/* Cart quantity policy: editing UX + per-variant caps.
 *
 * One file, loaded once with `defer`, holds everything that touches
 * <xo-cart-quantity> on the storefront. Previously these were three
 * separate inline IIFEs in layout/theme.liquid.
 *
 * Behavior covered (all unchanged from the inline version):
 *
 * 1. Allow free editing of every qty input. The theme's native
 *    xo-cart-quantity.handleInput coerces value → Number → if < 1,
 *    writes "1" back on every keystroke. That makes the empty-during-
 *    edit state impossible: backspace to empty snaps to "1", any new
 *    digit gets appended (producing 11/14/21 etc. — never a clean "5").
 *    We capture-phase `input` and stopImmediatePropagation on empty
 *    so the native handler never sees it; on blur we resolve invalid
 *    values to "1" and re-dispatch input+change so the cart syncs.
 *
 * 2. Visually disable xo-cart-quantity-plus when value >= host xo-max.
 *    Only hosts that declare xo-max get the treatment, so this scopes
 *    naturally to whatever products have a cap (currently just
 *    Americano Sample). The native +-button click is already a no-op
 *    past the cap; this gives the user a visible "you're at the limit"
 *    signal that matches what xo-cart-quantity-minus does at xo-min.
 *
 * 3. Per-variant hard cap at the fetch layer. /cart/add is incremental
 *    so we first call /cart.js for the current count, then clamp the
 *    requested quantity to remaining headroom (or block with a 422 if
 *    headroom is zero). /cart/change is absolute → clamp directly.
 *    This catches PDP add-to-cart click loops, direct typing past the
 *    input cap, and any third-party JS hitting the cart endpoints.
 *
 * Variants that need a cap go in CAPS below — each entry pairs the
 * Shopify variant id with the per-order maximum. Keep the host's
 * xo-max attribute (set in snippets/quantity.liquid via qty_max) in
 * sync so the UI cap and the network cap agree.
 *
 * 4. Wholesale minimums. Cart hosts that declare xo-min (rendered by
 *    the cart snippets for wholesale items) are enforced in the UI:
 *    typed/blurred values clamp UP to the minimum instead of down to 1,
 *    and any line that entered the cart below its minimum (e.g. added
 *    from a PDP at qty 1) is bumped up to it as soon as the cart
 *    renders, which re-syncs /cart/change through the theme's own
 *    handlers.
 */
(function () {
  if (window.__brisstaCartQtyPolicyWired) return;
  window.__brisstaCartQtyPolicyWired = true;

  // ── Per-variant cap registry ─────────────────────────────────────
  var CAPS = {
    '44003629531201': 3   // Americano Sample
  };

  // ── Disabled-look CSS for the plus button at xo-max ──────────────
  // Injected in JS rather than as a <style> tag in the layout so the
  // rule ships and lives with the behavior that drives it.
  var styleEl = document.createElement('style');
  styleEl.textContent =
    'xo-cart-quantity-plus[data-brissta-disabled]{opacity:.4;cursor:not-allowed;pointer-events:none}';
  document.head.appendChild(styleEl);

  // ── Shared helpers ───────────────────────────────────────────────
  function isCartQtyInput(el) {
    return el && el.tagName === 'INPUT' && el.type === 'number'
      && el.closest && el.closest('xo-cart-quantity');
  }
  function getMax(host) {
    var v = parseInt(host.getAttribute('xo-max'), 10);
    return isFinite(v) ? v : null;
  }
  function getMin(host) {
    var v = parseInt(host.getAttribute('xo-min'), 10);
    return isFinite(v) && v > 1 ? v : 1;
  }
  function updateDisabled(host) {
    var max = getMax(host);
    if (max === null) return;
    var input = host.querySelector('input[name="quantity"]');
    var plus = host.querySelector('xo-cart-quantity-plus');
    if (!input || !plus) return;
    var atMax = (parseInt(input.value, 10) || 0) >= max;
    if (atMax) {
      plus.setAttribute('data-brissta-disabled', '');
      plus.setAttribute('aria-disabled', 'true');
      plus.setAttribute('tabindex', '-1');
    } else {
      plus.removeAttribute('data-brissta-disabled');
      plus.setAttribute('aria-disabled', 'false');
      plus.setAttribute('tabindex', '0');
    }
  }
  function refreshAllDisabled() {
    document.querySelectorAll('xo-cart-quantity[xo-max]').forEach(updateDisabled);
  }

  // ── Fetch interceptor: cap /cart/add and /cart/change by variant ─
  var origFetch = window.fetch;
  var CART_PATH_RE = /\/cart\/(add|change)(\.js)?(\?|$|#)/;

  function getField(body, key) {
    if (body instanceof FormData || body instanceof URLSearchParams) return body.get(key);
    if (typeof body === 'string') {
      try { var j = JSON.parse(body); return j == null ? null : j[key]; } catch (e) {}
      try { return new URLSearchParams(body).get(key); } catch (e) {}
    }
    return null;
  }
  function setField(body, key, value) {
    if (body instanceof FormData || body instanceof URLSearchParams) {
      body.set(key, String(value));
      return body;
    }
    if (typeof body === 'string') {
      try { var j = JSON.parse(body); j[key] = value; return JSON.stringify(j); } catch (e) {}
      try { var p = new URLSearchParams(body); p.set(key, String(value)); return p.toString(); } catch (e) {}
    }
    return body;
  }
  function getVariantCountInCart(variantId) {
    return origFetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : { items: [] }; })
      .then(function (cart) {
        var n = 0;
        (cart.items || []).forEach(function (it) {
          if (String(it.variant_id) === variantId || String(it.id) === variantId) {
            n += (it.quantity || 0);
          }
        });
        return n;
      })
      .catch(function () { return 0; });
  }
  function makeError(status, message) {
    return new Response(JSON.stringify({ status: status, message: message, description: message }),
      { status: status, headers: { 'Content-Type': 'application/json' } });
  }

  window.fetch = function (input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var m = CART_PATH_RE.exec(url);
    if (!m) return origFetch.apply(this, arguments);

    var body = init && init.body;
    if (!body) return origFetch.apply(this, arguments);

    var id = String(getField(body, 'id') || '');
    var max = CAPS[id];
    if (!max) return origFetch.apply(this, arguments);

    var requested = parseInt(getField(body, 'quantity'), 10);
    if (!isFinite(requested)) return origFetch.apply(this, arguments);

    if (m[1] === 'change') {
      if (requested > max) {
        init = Object.assign({}, init, { body: setField(body, 'quantity', max) });
      }
      return origFetch.call(this, input, init);
    }
    // /cart/add — incremental, need current count to compute headroom.
    var ctx = this;
    return getVariantCountInCart(id).then(function (current) {
      var headroom = max - current;
      if (headroom <= 0) {
        return makeError(422, 'You already have the maximum of ' + max + ' of this item in your cart.');
      }
      if (requested > headroom) {
        init = Object.assign({}, init, { body: setField(body, 'quantity', headroom) });
      }
      return origFetch.call(ctx, input, init);
    });
  };

  // ── Input event: free-edit + live disabled-state refresh ─────────
  // Capture phase so we run BEFORE xo-cart-quantity.handleInput. Empty
  // value → stopImmediatePropagation (the native handler would force-
  // reset to "1"). Non-empty → fall through, refresh disabled state if
  // the host declares xo-max.
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!isCartQtyInput(t)) return;
    if (t.value === '') {
      e.stopImmediatePropagation();
      return;
    }
    var host = t.closest('xo-cart-quantity[xo-max]');
    if (host) updateDisabled(host);
  }, true);

  // ── Commit (blur / change): clamp to [xo-min, xo-max] and resync ─
  function clampOnCommit(e) {
    var t = e.target;
    if (!isCartQtyInput(t)) return;
    var host = t.closest('xo-cart-quantity');
    var n = Number(t.value);
    var max = host && getMax(host);
    var min = host ? getMin(host) : 1;
    var clamped = null;
    if (t.value === '' || !isFinite(n) || n < min) clamped = min;
    else if (max !== null && n > max) clamped = max;
    if (clamped !== null) {
      t.value = String(clamped);
      // Let xo-cart-quantity see the corrected value so its own
      // input/blur handlers fire /cart/change with the right number.
      t.dispatchEvent(new Event('input', { bubbles: true }));
      t.dispatchEvent(new Event('change', { bubbles: true }));
    }
    var capped = t.closest('xo-cart-quantity[xo-max]');
    if (capped) updateDisabled(capped);
  }
  document.addEventListener('change', clampOnCommit, true);
  document.addEventListener('blur', clampOnCommit, true);

  // +/- buttons mutate the input value via JS without firing 'input',
  // so re-check the disabled state on the next frame.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest
      && e.target.closest('xo-cart-quantity-plus, xo-cart-quantity-minus');
    if (!btn) return;
    var host = btn.closest('xo-cart-quantity[xo-max]');
    if (host) requestAnimationFrame(function () { updateDisabled(host); });
  }, true);

  // ── Wholesale minimums: never leave a cart line below its xo-min. ──
  // Hosts get xo-min from the cart snippets (wholesale items only). If a
  // line entered the cart below its minimum, bump it and dispatch
  // input+change so the theme's own handlers fire /cart/change. Skips
  // the focused input so we never stomp a value mid-edit (blur/change
  // clamping covers that case).
  function enforceMins() {
    document.querySelectorAll('xo-cart-quantity[xo-min]').forEach(function (host) {
      var input = host.querySelector('input[name="quantity"]');
      if (!input || input === document.activeElement) return;
      var min = getMin(host);
      var n = parseInt(input.value, 10);
      if (isFinite(n) && n >= min) return;
      input.value = String(min);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  // Initial pass + observe cart subtrees for re-renders (drawer
  // add/remove, /cart section refresh).
  function init() {
    refreshAllDisabled();
    enforceMins();
    var pending = null;
    var obs = new MutationObserver(function () {
      if (pending) return;
      pending = requestAnimationFrame(function () {
        pending = null;
        refreshAllDisabled();
        enforceMins();
      });
    });
    var roots = document.querySelectorAll('xo-cart-mini, .xo-cart-table, xo-cart');
    if (roots.length) {
      roots.forEach(function (r) { obs.observe(r, { childList: true, subtree: true }); });
    } else {
      obs.observe(document.body, { childList: true, subtree: true });
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
